import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from 'components/mui';
import {
  Paper,
  Skeleton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DashboardGreetingProps {
  userName: string;
  stats: { openCases: number; entriesToday: number; hoursToday: number };
  onRefresh: () => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getFormattedDate(): string {
  const now = new Date();
  const day = now.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th';
  return now.toLocaleDateString('en-GB', {
    weekday: 'long',
    month: 'long',
    year: 'numeric',
  }).replace(/(\d+)/, `${day}${suffix}`);
}

/* ------------------------------------------------------------------ */
/*  StatCard                                                           */
/* ------------------------------------------------------------------ */

function StatCard({
  icon,
  value,
  label,
  color,
}: {
  icon: React.ReactNode;
  value: number | string;
  label: string;
  color: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(color, 0.1),
          color,
          mr: 1.5,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */
/*  DashboardGreeting                                                  */
/* ------------------------------------------------------------------ */

function DashboardGreeting({ userName, stats, onRefresh }: DashboardGreetingProps) {
  const theme = useTheme();

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {getGreeting()}, {userName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getFormattedDate()}
          </Typography>
        </Box>
        <Tooltip title="Refresh dashboard">
          <IconButton onClick={onRefresh} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        <StatCard
          icon={<FolderOpenIcon />}
          value={stats.openCases}
          label="Open Cases"
          color={theme.palette.primary.main}
        />
        <StatCard
          icon={<EditNoteIcon />}
          value={stats.entriesToday}
          label="Entries Today"
          color={theme.palette.info.main}
        />
        <StatCard
          icon={<AccessTimeIcon />}
          value={stats.hoursToday}
          label="Hours Logged Today"
          color={theme.palette.success.main}
        />
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Loading skeleton                                                   */
/* ------------------------------------------------------------------ */

export function DashboardGreetingSkeleton() {
  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="text" width={280} height={32} />
        <Skeleton variant="text" width={200} height={20} />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="rounded" width={40} height={40} sx={{ mr: 1.5, borderRadius: 1.5 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={50} height={32} />
                <Skeleton variant="text" width={90} height={16} />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

export default DashboardGreeting;
